import { useEffect } from "react";

import { useState, useRef } from "react";
import { AnimatePresence, motion, useAnimationFrame, useMotionValue } from "framer-motion";

import { useLocation } from "react-router-dom";


import loadingLogo from "../assets/loading_logo.png"
import QuestionCard from "../components/questioncard";
import { allQuestions, newQuestions as newQuestionPool, Question, telQuestions, woyQuestions, couQuestions, totQuestions, sibQuestions } from "../models/question";
import AutoShrinkText from "../components/autoshrink";

import arrow from "../assets/arrow.png"
import pin from "../assets/pin.png"

import { fetchUserData, createQuiz, fetchCustomsData, trackClick } from "../lib/firebase";

import { analytics, logEvent, loadRemoteConfig } from "../lib/firebase";

import translations from "../translations";
import Button from "../components/button";

function isStorageAvailable(type) {
    try {
      const storage = window[type];
      const testKey = '__storage_test__';
      storage.setItem(testKey, testKey);
      storage.removeItem(testKey);
      return true;
    } catch (e) {
      return false; 
    }
  }

export default function Quiz() {

    const location = useLocation();
    const path = location.pathname;

    const [variant, setVariant] = useState('A');

    const [gid, setGID] = useState(null);

    const [questionIndex, setQuestionIndex] = useState(-2);

    const queryParameters = new URLSearchParams(window.location.search);
    const code = queryParameters.get("code");
    
    const [lang, setLang] = useState(queryParameters.get("lang") ?? "en")

    const [gameMode, setGameMode] = useState(queryParameters.get("mode") ?? "friend")

    const [cardTexts, setCardTexts] = useState(Array(10).fill(""));

    const [username, setUsername] = useState("");

    const inputRef = useRef(null);
    const [isFocused, setIsFocused] = useState(false);

    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [fetching, setFetching] = useState(false);

    const [questions, setQuestions] = useState([]);
    const [newQuestions, setNewQuestions] = useState(false);

    const [uploading, setUploading] = useState(false);

    const [storageAvailable, setStorageAvailable] = useState(true);

    const START_INDEX = -2;
    const NAME_INDEX = -1;
    const RESULT_INDEX = 10;

    function normalizeEmoji(char) {
        if (/[\u2600-\u27BF]/.test(char)) {
          return char + "\uFE0F";
        }
        return char;
      }

      function isEmoji(grapheme) {
        return /\p{Emoji}/u.test(grapheme);
      }

    const handleChange = (e) => {
        let value = e.target.value.slice(0, 24);
        if (gameMode == "tel" && questionIndex >= 0) {
            let chars = [...value].map(normalizeEmoji)
            chars = chars.filter(isEmoji)
            value = chars.length > 0 ? chars[0] : ""
        }
        if (questionIndex === NAME_INDEX) {
            setUsername(value);
        } else {
            const newTexts = [...cardTexts];
            newTexts[questionIndex] = value;
            setCardTexts(newTexts);
        }
        e.target.value = value
    };

    function decode(code) {
        if (code == null) { return "" }

        const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
        let result = 0;
      
        for (const char of code.toUpperCase()) {
          const index = alphabet.indexOf(char);
          if (index === -1) {
            return null;
          }
          result = result * 26 + index;
        }
      
        return result.toString().padStart(9, "0");
      }

    function hasSubmitted() {
        const key = gameMode !== "friend" ? gameMode : "quizzes";
        const submitted = JSON.parse(localStorage.getItem(key) || "[]");
        return submitted.includes(code);
    }

    function hasNewQuestions(currentQs) {
        if (gameMode !== "friend") return false;
        const lastQuestions = JSON.parse(localStorage.getItem("qs") || "{}");
        if (!lastQuestions[code]) return false;
        const savedIds = lastQuestions[code];
        return savedIds !== currentQs.join("");
    }

    function firstQuiz() {
        const submitted = JSON.parse(localStorage.getItem("quizzes") || "[]");
        return submitted.length == 0;
    }

    const [counter, setCounter] = useState(0);

    function getRandomElements(arr, n) {
        const shuffled = [...arr];
        for (let i = shuffled.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }
        return shuffled.slice(0, n);
      }

    async function fetchUser() {
        if (analytics) {
            logEvent(analytics, "web_quiz_loading_started", { mode: gameMode })
        }
        const interval = setInterval(() => {
            setCounter(prev => {
              if (prev >= 10) return -1;
              return prev + 1;
            });
          }, 400);

        loadRemoteConfig();

        const uid = decode(code)
        fetchUserData(uid)
        .then(async (data) => {
            setUser(data);
            setQuestionIndex(START_INDEX);
            if (Array.isArray(data.questions) && gameMode == "friend") {
                const userQuestionIds = data.questions;
                if (userQuestionIds.filter(qid => qid.startsWith('x')).length > 0) {
                    const customsData = await fetchCustomsData(uid)
                    const customs = customsData.map(item =>
                        new Question(
                            item.id,
                            "custom",
                            "text",
                            (lang) => item.text,
                            (name, lang) => "",
                            item.emoji
                        )
                      );
                    const userQuestions = userQuestionIds
                        .map(id => [...allQuestions, ...newQuestionPool, ...customs].find(q => q.id === id))
                        .filter(Boolean); 
                    setQuestions(userQuestions);
                } else {
                    const userQuestions = userQuestionIds
                        .map(id => [...allQuestions, ...newQuestionPool].find(q => q.id === id))
                        .filter(Boolean); 
                    setQuestions(userQuestions);
                }
            } else if (gameMode == "woy") {
                setQuestions(getRandomElements(woyQuestions, 10))
            } else if (gameMode == "tel") {
                setQuestions(getRandomElements(telQuestions, 10))
            } else if (gameMode == "cou") {
                setQuestions(getRandomElements(couQuestions, 10))
            } else if (gameMode == "tot") {
                setQuestions(getRandomElements(totQuestions, 10))
            } else if (gameMode == "sib") {
                setQuestions(getRandomElements(sibQuestions, 10))
            }
            const nxtQs = hasNewQuestions(data.questions ?? [])
            if (hasSubmitted() && (!nxtQs || gameMode != "friend")) {
                setQuestionIndex(RESULT_INDEX);
            }
            if (nxtQs) {
                setNewQuestions(true);
            }
            setLoading(false);
            setFetching(false);
            if (analytics) {
                if (data != null) {
                    if (data.friends >= 20 && gameMode == "friend") {
                        logEvent(analytics, "web_limit_reached", { mode: gameMode })
                    } else if ((gameMode == "woy" && data.friends < 5) || ((gameMode == "tel" || gameMode == "tot" || gameMode == "cou" || gameMode == "sib") && !data.plus)) {
                        logEvent(analytics, "web_game_not_unlocked", { mode: gameMode })
                    } else {
                        logEvent(analytics, "web_quiz_loaded", { mode: gameMode })
                    }
                } else {
                    logEvent(analytics, "web_quiz_loading_failed", { mode: gameMode })
                }
            }
            clearInterval(interval)
        })
        .catch((err) => {
            setLoading(false);
            setFetching(false);
            clearInterval(interval);
            if (analytics) {
                logEvent(analytics, "web_quiz_loading_failed", { mode: gameMode })
            }
        });
      }

      function markSubmitted() {
        const key = gameMode !== "friend" ? gameMode : "quizzes";
        const submitted = JSON.parse(localStorage.getItem(key) || "[]");
        if (!submitted.includes(code)) {
            submitted.push(code);
            localStorage.setItem(key, JSON.stringify(submitted));
        }
        if (gameMode == "friend") {
            const lastQuestions = JSON.parse(localStorage.getItem("qs") || "{}");
            lastQuestions[code] = questions.map(q => q.id).join("");
            localStorage.setItem("qs", JSON.stringify(lastQuestions));
        }
      }

      const uid = decode(code)

      function modeNumber() {
        if (gameMode == "friend") {
            return 0
        } else if (gameMode == "woy") {
            return 1
        } else if (gameMode == "tel") {
            return 2
        } else if (gameMode == "tot") {
            return 3
        } else if (gameMode == "cou") {
            return 4
        } else {
            return 5
        }
      }

      function nextButtonEnabled() {
        if (questionIndex == START_INDEX) {
            return false
        } else if (questionIndex == NAME_INDEX) {
            return username.length > 0
        } else if (questionIndex >= 0 && questionIndex < 10) {
            return cardTexts[questionIndex].length > 0
        } else {
            return false
        }
    }

    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    let storeLink = "https://apps.apple.com/us/app/bondz-friends-ranking/id6748461951"; // fallback link
  
    if (/android/i.test(userAgent)) {
      storeLink = "https://play.google.com/store/apps/details?id=de.davidschreiber.bondz&listing=finish";
    } else {
      storeLink = "https://apps.apple.com/us/app/bondz-test-your-friends/id6748461951?ppid=e26bd74c-eec3-4513-b177-4f059c8614d4";
    }

    function isMobile() {
        return navigator.userAgentData?.mobile || /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
      }

      async function submitAnswers() {
        const quizData = {
            about: uid,
            uid: `g${username}`,
            update: new Date(),
            questions: questions.map(q => q.id),
            answers: cardTexts,
            mode: modeNumber(),
            gid: gid
        }
        setUploading(true)
        try {
            const id = await createQuiz(quizData);
            markSubmitted();
            setQuestionIndex(questionIndex + 1);
        } catch (e) {
            
        } finally {
            setUploading(false);
        }
      }

    useEffect(() => {
        document.title = "BondZ Quiz";
        const available = isStorageAvailable('localStorage');
        setStorageAvailable(available);
        if (code != null && user == null && !fetching && code != null) {
            fetchUser()
        } else {
            setLoading(false)
        }
        let loadedVariant = localStorage.getItem('test_variant');
        if (!loadedVariant) {
            loadedVariant = Math.random() < 0.5 ? 'A' : 'B';
            localStorage.setItem('test_variant', loadedVariant);
        }
        setVariant(loadedVariant);
        let loadedGID = localStorage.getItem('gid');
        if (!loadedGID) {
            loadedGID = crypto.randomUUID().slice(0, 8);
            localStorage.setItem('gid', loadedGID);
        }
        setGID(loadedGID);
    }, []);

    const [progress, setProgress] = useState(0.0);
    const x = useMotionValue(0);

    useAnimationFrame(() => {
        const currentX = x.get();
        const maxDistance = 393 - 32 - 96 - 18;
        setProgress((currentX / maxDistance).toFixed(2));
      });

   

  function onEnterDown() {
    if (!loading && !uploading && nextButtonEnabled()) {
        if (questionIndex < RESULT_INDEX - 1) {
            if (questionIndex == START_INDEX) {
                if (analytics) {
                    logEvent(analytics, "web_quiz_started", { mode: gameMode })
                }
            }
            if (questionIndex == NAME_INDEX) {
                if (analytics) {
                    logEvent(analytics, "web_quiz_name_set", { mode: gameMode })
                }
            }
            if (questionIndex == 0) {
                if (analytics) {
                    logEvent(analytics, "web_quiz_q1_answered", { mode: gameMode })
                }
            }
            if (questionIndex == 2) {
                if (analytics) {
                    logEvent(analytics, "web_quiz_q3_answered", { mode: gameMode })
                }
            }
            if (questionIndex == 4) {
                if (analytics) {
                    logEvent(analytics, "web_quiz_q5_answered", { mode: gameMode })
                }
            }
            if (questionIndex == 6) {
                if (analytics) {
                    logEvent(analytics, "web_quiz_q7_answered", { mode: gameMode })
                }
            }
            const a = questionIndex + 1;
            setQuestionIndex(questionIndex + 1); 
            if (a >= 0 && a < questions.length) {
                if (questions[a].answerType == "text" || questions[a].answerType == "emoji") {
                    inputRef?.current?.focus()
                } else {
                    inputRef?.current?.blur();
                }
            } else if (a == NAME_INDEX) {
                inputRef?.current?.focus()
            } else {
                inputRef?.current?.blur();
            }
        } else {
            if (analytics) {
                if (firstQuiz()) {
                    logEvent(analytics, "web_quiz_finished_first", { mode: gameMode })
                }
                logEvent(analytics, "web_quiz_finished", { mode: gameMode })
            }
            submitAnswers()
        }
    }
  }

  function gameModeLocked() {
    return gameMode == "woy" && (user?.friends ?? 0) < 5
  }

  function limitReached() {
    return (user?.friends ?? 0) >= 20 && gameMode == "friend"
  }
      

    return (
        <motion.div className="quiz-wrapper"
        >
            <motion.div className="mobile-bound"
                
            >
                {user != null && questions.length == 10 ?
                    (limitReached()) ? 
                            <ErrorBox title={(translations[lang] || translations.en).limitReached.title} description={(translations[lang] || translations.en).limitReached.text(user.name)} />
                        
                    :
                        <div className="quiz-vstack">
                            <div className="quiz-box">
                                <input
                                    className="qtxtf"
                                        ref={inputRef}
                                        type="text"
                                        value={questionIndex === NAME_INDEX ? username : (cardTexts[questionIndex] ?? "")}
                                        onFocus={() => setIsFocused(true)}
                                        onBlur={() => setIsFocused(false)}
                                        onChange={handleChange}
                                        style={{
                                        position: "absolute",
                                        opacity: 0,
                                        pointerEvents: "none",
                                        }}
                                        autoComplete="off"
                                        autoCorrect="off"
                                        spellCheck={false}
                                        maxLength={25}
                                        disabled={loading || uploading}
                                        enterKeyHint="done"
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter") {
                                                onEnterDown()
                                            };
                                        }}
                                />
                                {questions.map((q, i) => {
                                    const index = i - questionIndex;
                                    const scale = i > questionIndex + 2 ? 0.1 : Math.min(Math.max(0, 1 - index * 0.1), 1)
                                    const yOffset = Math.min(index * -36, 0)
                                    const brightness = 1 - (index * 0.2)
                                    const xOffset = questionIndex > i ? 402 : 0
                                    return (
                                        <motion.div
                                        key={i}
                                        className="question-card__box"
                                        initial={{
                                            scale: scale,
                                            x: xOffset,
                                            y: yOffset,
                                            zIndex: 11 - i,
                                            filter: `brightness(${brightness})`
                                        }}
                                        animate={{
                                            scale: scale,
                                            x: xOffset,
                                            y: yOffset * scale,
                                            zIndex: 11 - i,
                                            filter: `brightness(${brightness})`
                                        }}
                                        transition={{
                                            type: "spring",
                                            stiffness: 160,  
                                            damping: 20,     
                                        }}
                                        >
                                            <QuestionCard question={q} name={user.name} key={i} style={{ top: `${i * 15}px` }} index={i} inputRef={inputRef} isFocused={isFocused} text={cardTexts[i]} lang={lang} answersName={username} gameMode={gameMode} cardTexts={cardTexts} setCardTexts={setCardTexts} currentIndex={questionIndex} />
                                        </motion.div>
                                    )
                                })}
                                <NameCard questionIndex={questionIndex} nameIndex={NAME_INDEX} user={user} inputRef={inputRef} username={username} gameMode={gameMode} isFocused={isFocused} lang={lang} />
                                <StartCard questionIndex={questionIndex} startIndex={START_INDEX} user={user} gameMode={gameMode} questions={questions} lang={lang} />
                                <ResultCard questionIndex={questionIndex} resultIndex={RESULT_INDEX} user={user} username={username} gameMode={gameMode} progress={progress} lang={lang} storeLink={storeLink} />
                            </div>
                            {!(questionIndex == START_INDEX || questionIndex == RESULT_INDEX) &&
                                <Controls questionIndex={questionIndex} setQuestionIndex={setQuestionIndex} startIndex={START_INDEX} nameIndex={NAME_INDEX} resultIndex={RESULT_INDEX} username={username} cardTexts={cardTexts} gameMode={gameMode} inputRef={inputRef} submitAnswers={submitAnswers} uploading={uploading} loading={loading} firstQuiz={firstQuiz} questions={questions} lang={lang} />
                            }
                        </div>
                :
                    !loading ?
                        storageAvailable ?
                            <ErrorBox title={(translations[lang] || translations.en).invalidLink.title} description={(translations[lang] || translations.en).invalidLink.text} />
                        :
                            <ErrorBox title={(translations[lang] || translations.en).incognitoMode.title} description={(translations[lang] || translations.en).incognitoMode.text} />
                    :
                    <div>
                        
                    </div>
                }
                {(questionIndex == START_INDEX || questionIndex == RESULT_INDEX) && !loading && !fetching &&
                    <QuizBottom lang={lang} user={user} questionIndex={questionIndex} setQuestionIndex={setQuestionIndex} nameIndex={NAME_INDEX} startIndex={START_INDEX} resultIndex={RESULT_INDEX} inputRef={inputRef} limitReached={limitReached} gameModeLocked={gameModeLocked} questions={questions} storeLink={storeLink} gameMode={gameMode} newQuestions={newQuestions} creatorUid={uid} />
                }
            </motion.div>
            {(loading || fetching || uploading) && 
                <div className="loading-indicator-wrapper">
                    <div className="loading-indicator">
                        <svg class="loading-indicator__spinner" viewBox="0 0 40 40">
                            <circle class="loading-indicator__spinner-track" cx="20" cy="20" r="16" />
                            <circle class="loading-indicator__spinner-circle" cx="20" cy="20" r="16" />
                        </svg>
                    </div>
                </div>
            }
            <QuizLoader loading={loading || fetching} lang={lang} />
        </motion.div>
    )
}

function QuizLoader(props) {

    const loading = props.loading
    const lang = props.lang

    return (
        <motion.div className="quiz-loading-wrapper"
            animate={{
                maskImage: loading ? [] : [
                "radial-gradient(circle at center, transparent 0px, #141414 0px)",
                "radial-gradient(circle at center, transparent 100vmax, #141414 100vmax)",
                ],
                WebkitMaskImage: loading ? [] : [
                "radial-gradient(circle at center, transparent 0px, #141414 0px)",
                "radial-gradient(circle at center, transparent 100vmax, #141414 100vmax)",
                ],
            }}
            transition={{
                duration: 1,
                delay: 0.4
            }}
            style={{ pointerEvents: "none" }}
        >
            <div className="quiz-loading">
            <div className="quiz-loading__logo">
                <img src={loadingLogo} />
            </div>
                <div className="quiz-loading__bottom">
                    <div>{(translations[lang] || translations.en).loadingQuiz}</div>
                    <div className="quiz-loading__bar">
                        <div className="quiz-loading__bar-back">
                            <motion.div className="quiz-loading__bar-front"
                                initial={{
                                    width: 16
                                }}
                                animate={{
                                    width: loading ? 100 : 184
                                }}
                                transition={{
                                    ease: "linear",
                                    duration: 1.0,
                                }}
                            ></motion.div>
                        </div>
                    </div>
                </div>
            </div>
        </motion.div>
    )
}

function QuizBottom(props) {

    const lang = props.lang

    const user = props.user
    const questionIndex = props.questionIndex
    const setQuestionIndex = props.setQuestionIndex
    const nameIndex = props.nameIndex
    const startIndex = props.startIndex
    const resultIndex = props.resultIndex
    const inputRef = props.inputRef

    const limitReached = props.limitReached
    const gameModeLocked = props.gameModeLocked

    const questions = props.questions

    const storeLink = props.storeLink

    const gameMode = props.gameMode

    const newQuestions = props.newQuestions

    const t = translations[lang] || translations.en

    function endText(name) {
        if (gameMode == "cou" || gameMode == "sib") return t.endFomoFriends(name)
        if (gameMode == "woy" || gameMode == "tel") return t.endFomoGame
        return t.endFomo(name)
    }

    function ctaText() {
        return (gameMode == "woy" || gameMode == "tel") ? t.continuePlaying : t.createQuiz.long
    }

    return (
        <div className="quiz-bottom__wrapper">
            <div className="quiz-bottom">
            {(user != null && gameMode == "friend" && questionIndex == startIndex && (user.friends ?? 0) > 1 && !(limitReached() || user == null || questions.length != 10)) ?
                <div className="quiz-bottom__description">{newQuestions ? (translations[lang] || translations.en).newQuiz(user.name ?? "") : (translations[lang] || translations.en).startFomoCount(user.friends ?? 0, user.name ?? "")}</div>
                :
                (user != null && gameMode == "friend" && questionIndex == startIndex && !(limitReached() || user == null || questions.length != 10)) ?
                <div className="quiz-bottom__description">{newQuestions ? (translations[lang] || translations.en).newQuiz(user.name ?? "") : (translations[lang] || translations.en).startFomo(user.name ?? "")}</div>
                    :
                    (user != null && questionIndex == resultIndex) ?
                    <div className="quiz-bottom__description">{endText(user.name ?? "")}</div>
                        :
                        (!(questionIndex == startIndex && !(limitReached() || user == null || questions.length != 10)) && ctaText() == t.createQuiz.long) ?
                        <div className="quiz-bottom__description">{t.endFomoNoName}</div>
                            : <div></div>
            }
            <Button text={questionIndex == startIndex && !(limitReached() || user == null || questions.length != 10) ? (translations[lang] || translations.en).startQuiz : ctaText() } repeatDelay={2.0} border={gameMode == "friend"} onClick={() => {
                    if (questionIndex == startIndex && !(limitReached() || user == null || questions.length != 10)) {
                        if (analytics) {
                            logEvent(analytics, "web_quiz_started", { mode: gameMode })
                        }
                        setQuestionIndex(questionIndex + 1); inputRef?.current?.focus();
                    } else {
                        window.open(storeLink, "_blank")
                        trackClick(props.creatorUid)
                        if (analytics) {
                            logEvent(analytics, "web_app_link_clicked", { platform: "android", location: "footer", mode: gameMode })
                        }
                    }
                }} />
                {questionIndex == startIndex && 
                    <div className="quiz-bottom__legal">
                        <a href="/terms"><div>{(translations[lang] || translations.en).terms}</div></a>
                        <a href="/privacy"><div>{(translations[lang] || translations.en).privacy}</div></a>
                    </div>
                }
            </div>
        </div>
    )
}


export function ErrorBox(props) {

    const title = props.title ?? "Error"
    const description = props.description ?? "Something went wrong"

    return (
        <div className="error-box">
            <div className="quiz-card__text font-white">{title}</div>
            <div className="quiz-card__description">{description}</div>
        </div>
    )
}

function ResultCard(props) {

    const questionIndex = props.questionIndex
    const resultIndex = props.resultIndex
    const user = props.user
    const username = props.username
    const gameMode = props.gameMode

    const progress = props.progress

    const lang = props.lang

    const index = resultIndex - questionIndex;
    const scale = resultIndex > questionIndex + 2 ? 0.1 : Math.min(Math.max(0, 1 - index * 0.1), 1)
    const yOffset = Math.min(index * -36, 0)
    const brightness = 1 - (index * 0.2)
    const xOffset = questionIndex > resultIndex ? 402 : 0

    function resultEmoji() {
        return "🔗"
        if (progress < 0.16) {
            return "🤡"
        } else if (progress < 0.5) {
            return "🤝"
        } else if (progress < 0.84) {
            return "🔗"
        } else {
            return "💕"
        }
      }

    return (
        <motion.div
            className="question-card__box"
            initial={{
                scale: scale,
                x: xOffset,
                y: yOffset,
                zIndex: 0,
                filter: `brightness(${brightness})`
            }}
            animate={{
                scale: scale,
                x: xOffset,
                y: yOffset * scale,
                zIndex: 0,
                filter: `brightness(${brightness})`
            }}
            transition={{
                type: "spring",
                stiffness: 160,  
                damping: 20,     
                }}
        >
            <div className="quiz-result-card">
                <div className="quiz-result-card__top"> 
                    <div className="quiz-result-card__user__vstack">
                        <UserImage size={85} name={user.name} image={user.image} />
                        <div>{user.name == null ? (translations[lang] || translations.en).yourFriend : user.name}</div>
                    </div>
                    {gameMode == "friend" && <div className="quiz-result-card__emoji">
                        {resultEmoji()}
                    </div>}
                    {gameMode == "woy" && <div className="quiz-result-card__emoji">
                        👉
                    </div>}
                    {gameMode == "tel" && <div className="quiz-result-card__emoji">
                        🔮
                    </div>}
                    {gameMode == "tot" && <div className="quiz-result-card__emoji">
                        💭
                    </div>}
                    {gameMode == "sib" && <div className="quiz-result-card__emoji">
                        🍼
                    </div>}
                    {gameMode == "cou" && <div className="quiz-result-card__emoji">
                        💘
                    </div>}
                    <div className="quiz-result-card__user__vstack"
                    >
                <UserImage size={85} name={username} image={null} />
                        <div>{username.length == 0 ? (translations[lang] || translations.en).you : username}</div>
                    </div>
                </div>
                <div className="quiz-result-card__mid">
                    {gameMode == "friend" &&
                        <motion.div className="quiz-result-card__mid__pin"
                        style={{
                            
                        }}
                            animate={{
                                x: [0, 402 - 80 - 80 - 12 - 12],
                                }}
                            transition={{
                            duration: 3.0,
                            repeat: Infinity,
                            repeatType: "reverse",
                            ease: "linear",
                            }}
                        >
                            <img src={pin} alt="!" />
                        </motion.div>        
                    }
            
                    {gameMode == "friend" &&
                        <div className="quiz-result-card__scale"> 
                            <div className="quiz-result-card__scale__grad"> 
                            
                            </div>
                        </div>
                    }
                    {gameMode != "friend" &&
                        <div className="quiz-result-card__boxes"> 
                        {Array.from({ length: 10 }).map((_, i) => (
                            <div className="checkbox_empty_small"></div>
                        ))}
                    </div>
                    }
                    
                </div>
                <div className="quiz-card__labels">
                    {gameMode == "friend" && <div className="quiz-card__text">{(translations[lang] || translations.en).quizCompleted.title}</div>}
                    {gameMode != "friend" && <div className="quiz-card__text">{(translations[lang] || translations.en).gameCompleted.title}</div>}
                    <div className="quiz-card__description">{(translations[lang] || translations.en).gameCompleted.text(user.name ?? "")}</div>
                </div>
            </div>
            
        </motion.div>
    )
}

function StartCard(props) {

    const questionIndex = props.questionIndex
    const user = props.user
    const gameMode = props.gameMode
    const questions = props.questions

    const lang = props.lang

    const startIndex = props.startIndex

    const index = startIndex - questionIndex;
    const scale = startIndex > questionIndex + 2 ? 0.1 : Math.min(Math.max(0, 1 - index * 0.1), 1)
    const yOffset = Math.min(index * -36, 0)
    const brightness = 1 - (index * 0.2)
    const xOffset = questionIndex > startIndex ? 402 : 0

    return (
        <motion.div
            className="question-card__box"
            initial={{
                scale: scale,
                x: xOffset,
                y: yOffset,
                zIndex: 11 - startIndex,
                filter: `brightness(${brightness})`
            }}
            animate={{
                scale: scale,
                x: xOffset,
                y: yOffset * scale,
                zIndex: 11 - startIndex,
                filter: `brightness(${brightness})`
            }}
            transition={{
                type: "spring",
                stiffness: 240,  
                damping: 20,     
                }}
                onClick={(e) => e.stopPropagation()}
            >
            <div className={gameMode == "friend" ? "quiz-start-card" : `quiz-start-card ${gameMode}`}>
                <div className="quiz-start-card__top">
                    <div className="quiz-start-card__images">
                        <div className="quiz-card__img__border">
                            <UserImage size={gameMode != "friend" && gameMode != "tot" ? 96 : 121} name={user.name} image={user.image} />
                        </div>
                        {gameMode != "friend" && gameMode != "tot" && 
                            <div className="quiz-card__img__border">
                                <UserImage size={96} name={null} image={null} />
                            </div>
                        }
                    </div>
                    <div className="quiz-card__labels">
                        {gameMode == "friend" && <div className={"quiz-card__text"}>{(translations[lang] || translations.en).quizOwner(user.name ?? "")}</div>}
                        {gameMode == "woy" && <div className={"quiz-card__text font-white"}>{(translations[lang] || translations.en).woy.title}</div>}
                        {gameMode == "tel" && <div className={"quiz-card__text font-white"}>{(translations[lang] || translations.en).tel.title}</div>}
                        {gameMode == "cou" && <div className={"quiz-card__text font-white"}>{(translations[lang] || translations.en).cou.title}</div>}
                        {gameMode == "tot" && <div className={"quiz-card__text font-white"}>{(translations[lang] || translations.en).tot.title}</div>}
                        {gameMode == "sib" && <div className={"quiz-card__text font-white"}>{(translations[lang] || translations.en).sib.title}</div>}
                        {gameMode == "friend" && <div className="quiz-card__description">{(translations[lang] || translations.en).startDescription(user.name ?? "")}</div>}
                        {gameMode == "woy" && <div className="quiz-card__description font-white-60">{(translations[lang] || translations.en).woy.description(user.name ?? "")}</div>}
                        {gameMode == "tel" && <div className="quiz-card__description font-white-60">{(translations[lang] || translations.en).tel.description(user.name ?? "")}</div>}
                        {gameMode == "cou" && <div className="quiz-card__description font-white-60">{(translations[lang] || translations.en).cou.description(user.name ?? "")}</div>}
                        {gameMode == "tot" && <div className="quiz-card__description font-white-60">{(translations[lang] || translations.en).tot.description(user.name ?? "")}</div>}
                        {gameMode == "sib" && <div className="quiz-card__description font-white-60">{(translations[lang] || translations.en).sib.description(user.name ?? "")}</div>}
                    </div>
                </div>
                <div className={gameMode == "friend" ? "quiz-start-card__bot" : "quiz-start-card__bot background-secondary"}>
                    {(questions.length < 10 ? allQuestions.slice(0, 10) : questions).map((q, i) => {
                        return (
                            <div>{q.emoji}</div>
                        )
                    })}
                    </div>
            </div>
            
            </motion.div>
    )
}

export function UserImage(props) {

    const size = props.size
    const name = props.name

    const image = props.image

    return (
        <motion.div className="user-image"
            style={{
                width: size,
                height: size,
                fontSize: size * 0.46
            }}
        >
            <div>{name != null && name.length > 0 ? name.charAt(0).toUpperCase() : "?" }</div>
            {image != null && <img src={image} />}
        </motion.div>
    )
}

function NameCard(props) {

    const questionIndex = props.questionIndex
    const nameIndex = props.nameIndex
    const user = props.user
    const inputRef = props.inputRef
    const username = props.username
    const gameMode = props.gameMode
    const isFocused = props.isFocused

    const lang = props.lang

    const index = nameIndex - questionIndex;
    const scale = nameIndex > questionIndex + 2 ? 0.1 : Math.min(Math.max(0, 1 - index * 0.1), 1)
    const yOffset = Math.min(index * -36, 0)
    const brightness = 1 - (index * 0.2)
    const xOffset = questionIndex > nameIndex ? 402 : 0

    return (
        <motion.div
            className="question-card__box"
            initial={{
                scale: scale,
                x: xOffset,
                y: yOffset,
                zIndex: 11 - nameIndex,
                filter: `brightness(${brightness})`
            }}
            animate={{
                scale: scale,
                x: xOffset,
                y: yOffset * scale,
                zIndex: 11 - nameIndex,
                filter: `brightness(${brightness})`
            }}
            transition={{
                type: "spring",
                stiffness: 240,  
                damping: 20,     
                }}
        >
            <div className="question-card">
                <div className={gameMode == "friend" ? "question-card__top name-card" : `question-card__top ${gameMode}`}>
                    <div>✏️</div>
                    <div>{(translations[lang] || translations.en).enterName.title}</div>
                </div>
                <div className="question-card__mid">
                    <div className="question-card__mid__type" 
                        onClick={() => {inputRef?.current?.focus()}}
                    >
                        <div className="question-card__mid__type_top">
                            <div className="question-card__mid__type__text"><AutoShrinkText text={username} /></div>
                            {isFocused ? <div className={gameMode == "friend" ? "question-card__mid__type_indicator name-card" : `question-card__mid__type_indicator ${gameMode}`}></div> : <div className="question-card__mid__type_indicator"></div>}
                        </div>
                        <div className="question-card__mid__type_bot"></div>
                    </div>
                </div>
                <div className="question-card__fot">
                    <div>
                    {(translations[lang] || translations.en).enterName.text(user.name ?? "")}
                    </div>
                </div>
            </div>
        </motion.div>
    )
}


function Controls(props) {

    const questionIndex = props.questionIndex
    const setQuestionIndex = props.setQuestionIndex
    const nameIndex = props.nameIndex
    const startIndex = props.startIndex
    const resultIndex = props.resultIndex
    const username = props.username
    const cardTexts = props.cardTexts
    const gameMode = props.gameMode
    const inputRef = props.inputRef

    const submitAnswers = props.submitAnswers

    const uploading = props.uploading
    const loading = props.loading

    const firstQuiz = props.firstQuiz

    const questions = props.questions

    const lang = props.lang

    function nextButtonEnabled() {
        if (questionIndex == startIndex) {
            return false
        } else if (questionIndex == nameIndex) {
            return username.length > 0
        } else if (questionIndex >= 0 && questionIndex < 10) {
            return cardTexts[questionIndex].length > 0
        } else {
            return false
        }
    }

    function nextButtonShown() {
        return questionIndex < resultIndex
    }

    return (
        <div className="quiz-controls">
            <div className="quiz-controls__box">
                
            </div>
                
            <div className="quiz-controls__box">
            {nextButtonEnabled() ?
                <motion.div className="quiz-controls__right__highlighted"
                    whileTap={{
                        scale: 0.92
                    }}
                    onClick={() => {
                        if (!loading && !uploading) {
                            if (questionIndex < resultIndex - 1) {
                                if (questionIndex == -2) {
                                    if (analytics) {
                                        logEvent(analytics, "web_quiz_started", { mode: gameMode })
                                    }
                                }
                                if (questionIndex == nameIndex) {
                                    if (analytics) {
                                        logEvent(analytics, "web_quiz_name_set", { mode: gameMode })
                                    }
                                }
                                if (questionIndex == 0) {
                                    if (analytics) {
                                        logEvent(analytics, "web_quiz_q1_answered", { mode: gameMode })
                                    }
                                }
                                if (questionIndex == 2) {
                                    if (analytics) {
                                        logEvent(analytics, "web_quiz_q3_answered", { mode: gameMode })
                                    }
                                }
                                if (questionIndex == 4) {
                                    if (analytics) {
                                        logEvent(analytics, "web_quiz_q5_answered", { mode: gameMode })
                                    }
                                }
                                if (questionIndex == 6) {
                                    if (analytics) {
                                        logEvent(analytics, "web_quiz_q7_answered", { mode: gameMode })
                                    }
                                }
                                const a = questionIndex + 1;
                                setQuestionIndex(a);
                                if (a >= 0 && a < questions.length) {
                                    if (questions[a].answerType == "text" || questions[a].answerType == "emoji") {
                                        inputRef?.current?.focus()
                                    } else {
                                        inputRef?.current?.blur();
                                    }
                                } else if (a == nameIndex) {
                                    inputRef?.current?.focus()
                                } else {
                                    inputRef?.current?.blur();
                                }
                            } else {
                                if (analytics) {
                                    if (firstQuiz()) {
                                        logEvent(analytics, "web_quiz_finished_first", { mode: gameMode })
                                    }
                                    logEvent(analytics, "web_quiz_finished", { mode: gameMode })
                                }
                                submitAnswers()
                            }
                        }
                    }}
                >
                    
                    <img src={arrow} alt="->" style={{ filter: "brightness(16%)" }} />
                </motion.div>
                    :
                    <div></div>
                    
            }
            </div>
                
        </div>
    )
}